<template>
  <div v-if="open" class="cart-drawer">
    <div class="cart-drawer__backdrop" @click="$emit('close')"></div>
    <aside class="cart-drawer__panel">
      <header class="cart-drawer__header">
        <h2>Your Cart</h2>
        <button class="cart-drawer__close" aria-label="Close cart" @click="$emit('close')">✕</button>
      </header>

      <p v-if="cart.error" class="cart-drawer__error">{{ cart.error }}</p>

      <div v-if="!cart.lines.length" class="cart-drawer__empty">
        <p>Your cart is empty.</p>
      </div>

      <ul v-else class="cart-drawer__lines">
        <li v-for="line in cart.lines" :key="line.id" class="cart-line">
          <img
            v-if="line.merchandise.image"
            :src="line.merchandise.image.url"
            :alt="line.merchandise.image.altText || line.merchandise.product.title"
            class="cart-line__img"
          />
          <div class="cart-line__body">
            <p class="cart-line__title">{{ line.merchandise.product.title }}</p>
            <p class="cart-line__price">{{ formatPrice(line.cost.totalAmount) }}</p>
          </div>
          <button class="cart-line__remove" :disabled="cart.loading" @click="remove(line.id)">Remove</button>
        </li>
      </ul>

      <footer v-if="cart.lines.length" class="cart-drawer__footer">
        <router-link to="/checkout" class="btn btn--dark cart-drawer__checkout" @click="$emit('close')">
          Checkout
        </router-link>
      </footer>
    </aside>
  </div>
</template>

<script>
import { useCart } from '../shopify/cart'
import { formatMoney } from '../shopify/money'

export default {
  name: 'CartDrawer',
  props: {
    open: { type: Boolean, default: false },
  },
  emits: ['close'],
  setup() {
    const { state, removeFromCart } = useCart()
    return { cart: state, removeFromCart }
  },
  methods: {
    formatPrice({ amount, currencyCode }) {
      return formatMoney(amount, currencyCode)
    },
    async remove(lineId) {
      try {
        await this.removeFromCart(lineId)
      } catch {
        // error surfaced via cart.error
      }
    },
  },
}
</script>

<style scoped>
.cart-drawer {
  position: fixed;
  inset: 0;
  z-index: 300;
}

.cart-drawer__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
}

.cart-drawer__panel {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: min(40rem, 100%);
  background: var(--canvas);
  display: flex;
  flex-direction: column;
  padding: 2.4rem;
  overflow-y: auto;
}

.cart-drawer__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2.4rem;
}

.cart-drawer__header h2 {
  font-size: 2rem;
  font-weight: 400;
}

.cart-drawer__close {
  background: none;
  border: none;
  font-size: 1.6rem;
  cursor: pointer;
}

.cart-drawer__error {
  color: #b3261e;
  font-size: 1.3rem;
  margin-bottom: 1.6rem;
}

.cart-drawer__empty {
  color: var(--stone);
  font-size: 1.4rem;
}

.cart-drawer__lines {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 1.6rem;
  flex: 1;
}

.cart-line {
  display: flex;
  align-items: center;
  gap: 1.2rem;
}

.cart-line__img {
  width: 6rem;
  height: 6rem;
  object-fit: contain;
  flex-shrink: 0;
  background: var(--paper);
}

.cart-line__body {
  flex: 1;
}

.cart-line__title {
  font-size: 1.4rem;
  margin-bottom: 0.3rem;
}

.cart-line__price {
  font-size: 1.3rem;
  color: var(--stone);
}

.cart-line__remove {
  background: none;
  border: none;
  color: var(--stone);
  font-size: 1.2rem;
  cursor: pointer;
  text-decoration: underline;
}

.cart-drawer__footer {
  margin-top: 2.4rem;
  padding-top: 2.4rem;
  border-top: 1px solid var(--linen);
}

.cart-drawer__checkout {
  display: flex;
  width: 100%;
}
</style>
